'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { useLocale, useTranslations } from 'next-intl';
import styles from './DateSelectorPopover.module.scss';
export interface DateSelectorPopoverProps {
  open: boolean;
  onClose: () => void;
  /** Selected date; `null` opens on today with nothing selected. */
  value: Dayjs | null;
  onChange: (date: Dayjs) => void;
  isDateDisabled?: (date: Dayjs) => boolean;
  minDate?: Dayjs;
  align?: 'left' | 'right';
  placement?: 'bottom' | 'top';
  anchor?: React.ReactNode;
  title?: string;
  popoverClassName?: string;
  /** Hide the top title/close row (month nav only). */
  hideHeader?: boolean;
  /** Su / Mo / … instead of single-letter weekday labels. */
  weekdayStyle?: 'narrow' | 'short';
}
const WEEKDAY_LABELS_NARROW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
const WEEKDAY_LABELS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;
const DateSelectorPopover: FC<DateSelectorPopoverProps> = ({
  open,
  onClose,
  value,
  onChange,
  isDateDisabled,
  minDate,
  align = 'left',
  placement = 'bottom',
  anchor,
  title,
  popoverClassName,
  hideHeader = false,
  weekdayStyle = 'narrow'
}) => {
  const t = useTranslations();
  const isRTL = useLocale() === 'ar';
  const rootRef = useRef<HTMLDivElement>(null);
  const initialMonth = (value ?? dayjs()).startOf('month');
  const [viewMonth, setViewMonth] = useState<Dayjs>(initialMonth);
  const weekdayLabels = weekdayStyle === 'short' ? WEEKDAY_LABELS_SHORT : WEEKDAY_LABELS_NARROW;
  useEffect(() => {
    if (open) setViewMonth((value ?? dayjs()).startOf('month'));
  }, [open, value]);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);
  const days = useMemo(() => {
    const firstOfMonth = viewMonth.startOf('month');
    const daysInMonth = viewMonth.daysInMonth();
    const leading = firstOfMonth.day(); // 0 = Sunday
    const cells: (Dayjs | null)[] = [];
    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(firstOfMonth.date(d));
    return cells;
  }, [viewMonth]);
  const isPrevDisabled = useMemo(() => {
    if (!minDate) return false;
    return viewMonth.isSame(minDate.startOf('month'), 'month') || viewMonth.isBefore(minDate.startOf('month'), 'month');
  }, [viewMonth, minDate]);
  const dayIsDisabled = (day: Dayjs) => {
    if (minDate && day.isBefore(minDate.startOf('day'))) return true;
    if (isDateDisabled) return isDateDisabled(day);
    return false;
  };
  const handleSelectDay = (day: Dayjs) => {
    if (dayIsDisabled(day)) return;
    onChange(day);
    onClose();
  };
  return <div ref={rootRef} className={styles.popoverRoot} data-sentry-component="DateSelectorPopover" data-sentry-source-file="DateSelectorPopover.tsx">
            {anchor}
            {open && <div className={`${styles.popover} ${popoverClassName ?? ''}`.trim()} data-align={align} data-placement={placement} role="dialog">
                    {!hideHeader && <div className={styles.header}>
                            <h3 className={styles.title}>
                                {title ?? t('booking.selectDate')}
                            </h3>
                            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
                                <X size={16} strokeWidth={2} />
                            </button>
                        </div>}

                    <div className={styles.navRow}>
                        <button type="button" className={styles.navBtn} onClick={() => setViewMonth(m => m.subtract(1, 'month'))} disabled={isPrevDisabled} aria-label="Previous month">
                            {isRTL ? <ChevronRight size={16} strokeWidth={2} /> : <ChevronLeft size={16} strokeWidth={2} />}
                        </button>
                        <p className={styles.monthLabel}>
                            {viewMonth.format('MMMM YYYY')}
                        </p>
                        <button type="button" className={styles.navBtn} onClick={() => setViewMonth(m => m.add(1, 'month'))} aria-label="Next month">
                            {isRTL ? <ChevronLeft size={16} strokeWidth={2} /> : <ChevronRight size={16} strokeWidth={2} />}
                        </button>
                    </div>

                    <div className={styles.weekdayRow}>
                        {weekdayLabels.map((label, i) => <div key={i} className={styles.weekday}>
                                {label}
                            </div>)}
                    </div>

                    <div className={styles.grid}>
                        {days.map((day, i) => {
          if (!day) {
            return <div key={i} className={styles.dayCell} />;
          }
          const selected = !!value && day.isSame(value, 'day');
          const isToday = day.isSame(dayjs(), 'day');
          return <div key={i} className={styles.dayCell}>
                                    <button type="button" className={`${styles.day} ${selected ? styles.daySelected : ''} ${isToday && !selected ? styles.dayToday : ''}`} onClick={() => handleSelectDay(day)} disabled={dayIsDisabled(day)}>
                                        {day.date()}
                                    </button>
                                </div>;
        })}
                    </div>
                </div>}
        </div>;
};
export default DateSelectorPopover;